import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Phone, Mail, Sparkles, X, Check, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ContactDialog } from '../components/ContactDialog';
import { SEO } from '../components/SEO';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProductDetail = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const lang = i18n.language;

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center" data-testid="product-loading">
        <p className="font-body text-muted-foreground">{t('gallery.loading')}</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-32 flex flex-col items-center justify-center" data-testid="product-not-found">
        <p className="font-body text-muted-foreground mb-6">{t('product.notFound')}</p>
        <Link to="/gallery">
          <Button>{t('product.back')}</Button>
        </Link>
      </div>
    );
  }

  const getCategoryName = (category) => {
    const names = {
      'ru': {
        'rings': 'Помолвочные кольца',
        'earrings': 'Серьги-пусеты',
        'bracelets': 'Эксклюзивные браслеты'
      },
      'lv': {
        'rings': 'Saderināšanās gredzeni',
        'earrings': 'Auskari',
        'bracelets': 'Ekskluzīvas aproces'
      }
    };
    return names[lang]?.[category] || category;
  };

  const inStock = product.in_stock !== false;
  const stockLabel = inStock 
    ? { ru: 'В наличии', lv: 'Noliktavā' }
    : { ru: 'Под заказ', lv: 'Pēc pasūtījuma' };

  const openLightbox = (imageUrl) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage(null);
  };

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="product-detail-page">
      <SEO 
        title={product.name[lang] || product.name.ru}
        description={product.description[lang] || product.description.ru}
        image={product.images[0]}
        type="product"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Link to="/gallery">
          <Button variant="ghost" className="mb-8 uppercase tracking-widest" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('product.back')}
          </Button>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <div 
              className="aspect-square overflow-hidden bg-secondary rounded-sm cursor-zoom-in" 
              data-testid="main-product-image"
              onClick={() => openLightbox(product.images[selectedImage])}
            >
              <motion.img
                key={selectedImage}
                src={product.images[selectedImage]}
                alt={product.name[lang] || product.name.ru}
                className="w-full h-full object-contain"
                loading="lazy"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              />
            </div>

            {product.images.length > 1 && (
              <div className="grid grid-cols-4 gap-4" data-testid="product-thumbnails">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`aspect-square overflow-hidden border-2 rounded-sm transition-all duration-300 cursor-pointer ${
                      selectedImage === index ? 'border-accent' : 'border-border'
                    }`}
                    data-testid={`thumbnail-${index}`}
                  >
                    <img
                      src={image}
                      alt={`${product.name[lang] || product.name.ru} ${index + 1}`}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col"
          >
            <div className="mb-4">
              <p className="font-body text-sm text-muted-foreground uppercase tracking-widest mb-2" data-testid="product-category">
                {getCategoryName(product.category)}
              </p>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-6 tracking-tight" data-testid="product-name">
                {product.name[lang] || product.name.ru}
              </h1>
              
              {/* Статус товара - В наличии / Под заказ */}
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm ${
                inStock 
                  ? 'bg-green-600 text-white' 
                  : 'bg-amber-600 text-white'
              }`} data-testid="product-stock-status">
                {inStock ? <Check className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                <span>{stockLabel[lang]}</span>
              </div>
            </div>

            <div className="mb-8">
              {product.original_price && product.original_price > product.price && (
                <div className="inline-block px-3 py-1 bg-accent text-accent-foreground text-sm font-bold uppercase tracking-wider rounded mb-3">
                  {lang === 'lv' ? 'Akcija' : 'Акция'}
                </div>
              )}
              <div className="flex items-baseline gap-3">
                <p className="font-heading text-3xl md:text-4xl text-accent" data-testid="product-price">
                  €{product.price.toLocaleString(lang === 'lv' ? 'lv-LV' : 'ru-RU')}
                </p>
                {product.original_price && product.original_price > product.price && (
                  <p className="font-heading text-2xl md:text-3xl text-muted-foreground line-through">
                    €{product.original_price.toLocaleString(lang === 'lv' ? 'lv-LV' : 'ru-RU')}
                  </p>
                )}
              </div>
              {product.sizes && product.sizes.length > 0 && (
                <div className="mt-4">
                  <p className="font-body text-sm text-muted-foreground mb-2">
                    {lang === 'lv' ? 'Pieejamie izmēri:' : 'Доступные размеры:'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {product.sizes.map((size, index) => (
                      <span
                        key={index}
                        className="inline-block px-3 py-1 border border-border rounded text-sm font-body hover:border-accent transition-colors"
                      >
                        {size}
                      </span>
                    ))}
                  </div>
                  <p className="font-body text-xs text-muted-foreground italic mt-2">
                    {lang === 'lv' 
                      ? 'Izmēru var palielināt vai samazināt - tas nav problēma.' 
                      : 'Размер можно увеличить или уменьшить - это не проблема.'}
                  </p>
                </div>
              )}
              {product.popularity > 85 && (
                <div className="flex items-center gap-2 text-accent mt-3">
                  <Sparkles className="w-4 h-4" />
                  <span className="font-body text-sm uppercase tracking-wide" data-testid="popular-badge">
                    {t('product.popular')}
                  </span>
                </div>
              )}
            </div>

            <div className="mb-12 pb-12 border-b border-border">
              <h3 className="font-heading text-xl mb-6 tracking-tight">{t('product.characteristics')}</h3>
              <div className="space-y-4" data-testid="product-description">
                <p className="font-body text-base text-foreground leading-relaxed pb-6 border-b border-border/50 whitespace-pre-line">
                  {product.description[lang] || product.description.ru}
                </p>
                <div className="flex items-start gap-3 py-3 border-b border-border/50">
                  <div className="w-2 h-2 rounded-full bg-accent mt-2 flex-shrink-0" />
                  <div className="flex justify-between items-center flex-1">
                    <span className="font-body text-base text-foreground">{t('product.category')}</span>
                    <span className="font-body text-base text-muted-foreground">{getCategoryName(product.category)}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3 py-3 border-b border-border/50">
                  <div className="w-2 h-2 rounded-full bg-accent mt-2 flex-shrink-0" />
                  <div className="flex justify-between items-center flex-1">
                    <span className="font-body text-base text-foreground">
                      {lang === 'lv' ? 'Pieejamība' : 'Наличие'}
                    </span>
                    <span className={`font-body text-base font-medium ${
                      inStock ? 'text-green-600' : 'text-amber-600'
                    }`}>
                      {stockLabel[lang]}
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-3 py-3 border-b border-border/50">
                  <div className="w-2 h-2 rounded-full bg-accent mt-2 flex-shrink-0" />
                  <div className="flex justify-between items-center flex-1">
                    <span className="font-body text-base text-foreground">{t('product.sku')}</span>
                    <span className="font-body text-base text-muted-foreground">{product.id.toUpperCase()}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <Button
                size="lg"
                onClick={() => setContactOpen(true)}
                className="w-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground uppercase tracking-widest transition-colors duration-500"
                data-testid="inquire-button"
              >
                <Mail className="w-4 h-4 mr-2" />
                {t('product.inquire')}
              </Button>
              
              <a href={`tel:${t('nav.phone')}`} className="block">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full border-2 uppercase tracking-widest transition-colors duration-500"
                  data-testid="call-button"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  {t('nav.phone')}
                </Button>
              </a>
            </div>

            <div className="mt-12 p-6 bg-muted rounded-sm">
              <h3 className="font-heading text-lg mb-3 tracking-tight">{t('product.deliveryTitle')}</h3>
              <p className="font-body text-sm text-muted-foreground leading-relaxed">
                {t('product.deliveryText')}
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <ContactDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        productId={product.id}
        productName={product.name[lang] || product.name.ru}
      />

      {/* Lightbox для полноразмерного просмотра изображений */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white hover:text-accent transition-colors z-10"
              aria-label="Close"
            >
              <X className="w-8 h-8" />
            </button>
            
            <motion.img
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              src={lightboxImage}
              alt={product.name[lang] || product.name.ru}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
